// Phase 64 — Onboarding wizard. Single long page rendered at /onboarding.
// Two entry-points share this page:
//   1. First-manager flow — Login redirects here when manager.onboarding_completed=false.
//   2. Self-signup — Same redirect after /signup/verify lands the manager.
//
// Architecture:
//   * Each section is a small inline component with its own load/save against
//     existing tenant endpoints (settings PUT, /staff POST, /templates POST,
//     /admin/backup/config PUT). The wizard does NOT proxy data through a
//     single mega-endpoint — that would have meant a parallel save path next
//     to the existing settings page, which would drift.
//   * "Why this matters" sidebar updates on focus via a shared
//     `setFocusHint` setter passed down to each field.
//   * Progress meter calls /api/onboarding/status whenever a section saves
//     so the percent reflects the latest state without manual recompute.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CONNECT_ENABLED } from "@/lib/featureFlags";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, Palette, FileText as FileTextIcon, Landmark, UsersRound,
  Wrench, Plug, CloudDownload, ChevronDown, ChevronUp, Check, HelpCircle,
  Sparkles, ArrowRight, Save,
} from "lucide-react";
import { toast } from "sonner";

// Field-level tooltip examples used both inline and in the side panel.
const TOOLTIPS = {
  company_name: { hint: "The legal business name shown on every invoice + email.", example: "Smith Pump Hire Ltd" },
  company_email: { hint: "Customers will reply here. Goes on invoices.", example: "info@smithpumps.ie" },
  vat_number: { hint: "Required on invoices for VAT-registered companies in Ireland.", example: "IE1234567T" },
  rct_number: { hint: "Revenue Construction Tax. Only relevant if you operate under RCT.", example: "Optional" },
  cro_number: { hint: "Companies Registration Office number (Republic of Ireland).", example: "Optional" },
  phone: { hint: "Main contact phone — appears on invoices + email signatures.", example: "+353 87 555 0100" },
  website: { hint: "Your business website.", example: "https://smithpumps.ie" },
  iban: { hint: "Bank account for customer transfers. Shown on invoice footer.", example: "IE12 BOFI 9000 1234 5678" },
  bic: { hint: "Bank Identifier Code (sometimes called SWIFT).", example: "BOFIIE2D" },
  bank_name: { hint: "The bank holding the account above.", example: "Bank of Ireland" },
  accent_color: { hint: "Primary brand color — used on invoice headers + email accent buttons.", example: "#0ea5e9" },
  invoice_prefix: { hint: "Prefix on every invoice number. Most users keep the default.", example: "I-" },
  payment_terms: { hint: "Default payment terms shown on invoices.", example: "Net 14 days" },
  accounts_email: { hint: "When set, your customer's accounts department is CC'd on every invoice.", example: "accounts@customer.ie" },
  ors_api_key: { hint: "Optional. Get a free key at openrouteservice.org for live distance + travel-time calculations.", example: "5b3ce..." },
  staff_pin: { hint: "4-digit PIN your staff member uses to log in to their portal.", example: "1234" },
};

const SECTION_KEYS = [
  "company_basics", "branding", "invoicing", "bank_details",
  "staff", "templates", "integrations", "backup",
];

const SECTION_META = {
  company_basics: { label: "Company basics", icon: Building2 },
  branding: { label: "Branding", icon: Palette },
  invoicing: { label: "Invoicing", icon: FileTextIcon },
  bank_details: { label: "Bank details", icon: Landmark },
  staff: { label: "Staff", icon: UsersRound },
  templates: { label: "Job templates", icon: Wrench },
  integrations: { label: "Optional integrations", icon: Plug },
  backup: { label: "Daily backup", icon: CloudDownload },
};


export default function Onboarding() {
  const navigate = useNavigate();
  const { manager } = useAuth();
  const [status, setStatus] = useState({ completed: false, percent: 0, missing_critical: [], sections: {} });
  const [openSection, setOpenSection] = useState("company_basics");
  const [focusHint, setFocusHint] = useState({
    title: "Welcome to JobsBook",
    body: "Every field on this page powers something real — invoices, emails, reports, staff portals. The more you fill in now, the less you'll have to backfill from inside Settings later. Only Company name + Company email are mandatory.",
    example: null,
  });

  const refresh = async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { refresh(); }, []);

  const showHint = (key) => {
    const t = TOOLTIPS[key];
    if (!t) return;
    setFocusHint({ title: SECTION_META[openSection]?.label || "Why this matters", body: t.hint, example: t.example });
  };

  const onSectionSaved = async () => {
    await refresh();
  };

  const completeNow = async () => {
    try {
      await api.post("/onboarding/complete");
      toast.success("Setup complete · welcome aboard");
      navigate("/calendar");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const skipForNow = async () => {
    try {
      await api.post("/onboarding/skip");
      toast.info("You can come back here from the dashboard banner anytime.");
      navigate("/calendar");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/40 to-background" data-testid="onboarding-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Hero */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-[12px] bg-sky-500 text-white grid place-items-center text-2xl font-extrabold shadow-sm">
              <Sparkles className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                Welcome to JobsBook
              </h1>
              <p className="text-muted-foreground text-sm mt-1 max-w-xl">
                Hi {manager?.name || "there"}, let&apos;s get your tenant set up.
                This takes 5 minutes — you can skip any section and come back to it.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 min-w-[180px]" data-testid="onboarding-progress-meter">
            <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">Setup progress</div>
            <div className="text-2xl font-extrabold mono tabular-nums" data-testid="onboarding-progress-percent">{status.percent}%</div>
            <div className="h-2 w-full bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-500 transition-all duration-500"
                style={{ width: `${Math.max(2, status.percent)}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          {/* Sections (2/3) */}
          <main className="lg:col-span-2 space-y-3">
            {SECTION_KEYS.map((key) => (
              <SectionWrapper
                key={key}
                sectionKey={key}
                isOpen={openSection === key}
                onToggle={() => setOpenSection(openSection === key ? "" : key)}
                completed={!!status.sections?.[key]}
                showHint={showHint}
                onSaved={onSectionSaved}
              />
            ))}

            {/* Footer actions */}
            <div className="bg-card border border-border rounded-[12px] p-5 mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                {status.missing_critical?.length > 0
                  ? `${status.missing_critical.length} thing${status.missing_critical.length === 1 ? "" : "s"} still missing — you can finish later.`
                  : "Everything looks good — you're ready to start booking."}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={skipForNow} data-testid="onboarding-skip-btn">
                  Skip and explore
                </Button>
                <Button onClick={completeNow} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onboarding-complete-btn">
                  <Check className="h-4 w-4 mr-1" /> Complete setup
                </Button>
              </div>
            </div>
          </main>

          {/* "Why this matters" panel (1/3 on desktop, fixed strip on mobile) */}
          <aside className="hidden lg:block" data-testid="onboarding-why-panel">
            <div className="sticky top-6 bg-sky-50 border border-sky-200 rounded-[12px] p-5">
              <div className="text-[11px] mono uppercase tracking-widest text-sky-700 font-bold">
                Why this matters
              </div>
              <h3 className="text-lg font-extrabold mt-1 text-sky-900" data-testid="onboarding-hint-title">{focusHint.title}</h3>
              <p className="text-sm text-sky-900/80 mt-2 leading-relaxed" data-testid="onboarding-hint-body">{focusHint.body}</p>
              {focusHint.example && (
                <div className="mt-3 text-[11px] mono uppercase tracking-widest text-sky-700">Example</div>
              )}
              {focusHint.example && (
                <div className="mono text-sm text-sky-900 mt-1 bg-sky-100/60 rounded px-2 py-1 inline-block" data-testid="onboarding-hint-example">
                  {focusHint.example}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}


// ---------- Section wrapper -------------------------------------------------
function SectionWrapper({ sectionKey, isOpen, onToggle, completed, showHint, onSaved }) {
  const meta = SECTION_META[sectionKey];
  const Icon = meta.icon;
  return (
    <section
      className={`bg-card border rounded-[12px] overflow-hidden transition-colors ${
        completed ? "border-emerald-200" : "border-border"
      }`}
      data-testid={`onboarding-section-${sectionKey}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-secondary/30 transition-colors"
        data-testid={`onboarding-section-toggle-${sectionKey}`}
      >
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-[8px] grid place-items-center ${completed ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>
            {completed ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
          </div>
          <div>
            <div className="font-extrabold">{meta.label}</div>
            <div className="text-[11px] mono uppercase tracking-widest text-muted-foreground">
              {completed ? "Looks good" : "Click to open"}
            </div>
          </div>
        </div>
        {isOpen ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-1 border-t border-border" data-testid={`onboarding-section-body-${sectionKey}`}>
          <SectionBody sectionKey={sectionKey} showHint={showHint} onSaved={onSaved} />
        </div>
      )}
    </section>
  );
}


function SectionBody({ sectionKey, showHint, onSaved }) {
  if (sectionKey === "company_basics") return <CompanyBasicsForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "branding") return <BrandingForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "invoicing") return <InvoicingForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "bank_details") return <BankDetailsForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "staff") return <StaffForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "templates") return <TemplatesForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "integrations") return <IntegrationsForm showHint={showHint} onSaved={onSaved} />;
  if (sectionKey === "backup") return <BackupForm showHint={showHint} onSaved={onSaved} />;
  return null;
}


// Small helper: labeled field with tooltip + onFocus side-panel hint.
function LField({ label, tipKey, showHint, children, testId }) {
  const tip = TOOLTIPS[tipKey];
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center gap-1">
        <Label className="text-[11px] uppercase tracking-wider font-bold">{label}</Label>
        {tip && (
          <span
            title={`${tip.hint}${tip.example ? " · e.g. " + tip.example : ""}`}
            className="text-muted-foreground hover:text-foreground cursor-help"
          >
            <HelpCircle className="h-3 w-3" />
          </span>
        )}
      </div>
      <div onFocus={() => showHint(tipKey)}>{children}</div>
      {tip?.example && (
        <div className="text-[10px] mono text-muted-foreground">e.g. <span className="text-foreground/80">{tip.example}</span></div>
      )}
    </div>
  );
}


// ---------- Section 1: Company basics --------------------------------------
function CompanyBasicsForm({ showHint, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data)).catch(() => setData({ company: {} }));
  }, []);
  if (!data) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;
  const co = data.company || {};
  const set = (k, v) => setData((d) => ({ ...d, company: { ...(d.company || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        company: { ...(data.company || {}) },
      };
      await api.put("/settings", payload);
      toast.success("Company basics saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LField label="Company name *" tipKey="company_name" showHint={showHint} testId="onb-cb-name-field">
          <Input value={co.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Smith Pump Hire Ltd" data-testid="onb-company-name" />
        </LField>
        <LField label="Company email *" tipKey="company_email" showHint={showHint} testId="onb-cb-email-field">
          <Input value={co.email || ""} onChange={(e) => set("email", e.target.value)} placeholder="info@yourbusiness.ie" data-testid="onb-company-email" />
        </LField>
        <LField label="Phone" tipKey="phone" showHint={showHint}>
          <Input value={co.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="onb-company-phone" />
        </LField>
        <LField label="Website" tipKey="website" showHint={showHint}>
          <Input value={co.website || ""} onChange={(e) => set("website", e.target.value)} data-testid="onb-company-website" />
        </LField>
        <LField label="Address" tipKey="company_name" showHint={showHint}>
          <Textarea value={co.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} data-testid="onb-company-address" />
        </LField>
        <div className="grid grid-cols-1 gap-3">
          <LField label="VAT number" tipKey="vat_number" showHint={showHint}>
            <Input value={co.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} data-testid="onb-vat" />
          </LField>
          <LField label="RCT number" tipKey="rct_number" showHint={showHint}>
            <Input value={co.rct_number || ""} onChange={(e) => set("rct_number", e.target.value)} data-testid="onb-rct" />
          </LField>
          <LField label="CRO number" tipKey="cro_number" showHint={showHint}>
            <Input value={co.cro_number || ""} onChange={(e) => set("cro_number", e.target.value)} data-testid="onb-cro" />
          </LField>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-cb-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save company basics"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Section 2: Branding ---------------------------------------------
function BrandingForm({ showHint, onSaved }) {
  const [color, setColor] = useState("#0ea5e9");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [logoPath, setLogoPath] = useState(null);
  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setColor(data?.invoice_template?.accent_color || "#0ea5e9");
      setLogoPath(data?.logo_path || null);
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: cur } = await api.get("/settings");
      await api.put("/settings", {
        ...cur,
        invoice_template: { ...(cur.invoice_template || {}), accent_color: color },
      });
      toast.success("Brand color saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("logo", file);
    try {
      const { data } = await api.post("/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoPath(data?.logo_path || null);
      toast.success("Logo uploaded");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <LField label="Brand accent color" tipKey="accent_color" showHint={showHint}>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-14 rounded border border-border cursor-pointer"
              data-testid="onb-brand-color"
            />
            <Input value={color} onChange={(e) => setColor(e.target.value)} className="font-mono text-sm flex-1" />
          </div>
        </LField>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wider font-bold">Logo</Label>
          {logoPath ? (
            <div className="text-[11px] text-muted-foreground" data-testid="onb-logo-current">
              Current logo: <span className="mono">{logoPath.split("/").pop()}</span>
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground italic">No logo uploaded yet.</div>
          )}
          <div className="flex items-center gap-2 mt-1">
            <input ref={fileRef} type="file" accept="image/*" className="text-xs" data-testid="onb-logo-input" />
            <Button variant="outline" size="sm" onClick={uploadLogo} data-testid="onb-logo-upload-btn">Upload</Button>
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-brand-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save brand color"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Section 3: Invoicing --------------------------------------------
function InvoicingForm({ showHint, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data));
  }, []);
  if (!data) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setTpl = (k, v) => setData((d) => ({ ...d, invoice_template: { ...(d.invoice_template || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", data);
      toast.success("Invoicing defaults saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LField label="Invoice number prefix" tipKey="invoice_prefix" showHint={showHint}>
          <Input
            value={data.invoice_number_prefix || "I-"}
            onChange={(e) => set("invoice_number_prefix", e.target.value)}
            data-testid="onb-invoice-prefix"
          />
        </LField>
        <LField label="Default payment terms" tipKey="payment_terms" showHint={showHint}>
          <Input
            value={data.invoice_template?.payment_terms || ""}
            onChange={(e) => setTpl("payment_terms", e.target.value)}
            placeholder="e.g. Net 14 days"
            data-testid="onb-payment-terms"
          />
        </LField>
        <LField label="Accounts dept email" tipKey="accounts_email" showHint={showHint}>
          <Input
            value={data.invoice_template?.accounts_dept_email || ""}
            onChange={(e) => setTpl("accounts_dept_email", e.target.value)}
            data-testid="onb-accounts-email"
          />
        </LField>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-invoicing-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save invoicing defaults"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Section 4: Bank details -----------------------------------------
function BankDetailsForm({ showHint, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data));
  }, []);
  if (!data) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", data);
      toast.success("Bank details saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <LField label="Bank name" tipKey="bank_name" showHint={showHint}>
          <Input value={data.invoice_bank_name || ""} onChange={(e) => set("invoice_bank_name", e.target.value)} data-testid="onb-bank-name" />
        </LField>
        <LField label="IBAN" tipKey="iban" showHint={showHint}>
          <Input value={data.invoice_bank_iban || ""} onChange={(e) => set("invoice_bank_iban", e.target.value)} className="font-mono" data-testid="onb-bank-iban" />
        </LField>
        <LField label="BIC" tipKey="bic" showHint={showHint}>
          <Input value={data.invoice_bank_bic || ""} onChange={(e) => set("invoice_bank_bic", e.target.value)} className="font-mono" data-testid="onb-bank-bic" />
        </LField>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-bank-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save bank details"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Section 5: Staff ------------------------------------------------
function StaffForm({ showHint, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [draft, setDraft] = useState({ name: "", pin: "" });
  const [adding, setAdding] = useState(false);

  const refresh = async () => {
    try { const { data } = await api.get("/staff"); setStaff(data || []); } catch (_e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!draft.name.trim() || !/^\d{4}$/.test(draft.pin)) {
      toast.error("Name + 4-digit PIN are required.");
      return;
    }
    setAdding(true);
    try {
      await api.post("/staff", { name: draft.name.trim(), pin: draft.pin });
      toast.success(`Staff member added: ${draft.name}`);
      setDraft({ name: "", pin: "" });
      await refresh();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  return (
    <div className="space-y-4">
      {staff.length > 0 && (
        <div className="space-y-1" data-testid="onb-staff-list">
          {staff.map((s) => (
            <div key={s.id} className="flex items-center justify-between bg-secondary/30 rounded px-3 py-1.5 text-sm">
              <span className="font-semibold">{s.name}</span>
              <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">PIN ••••</span>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end">
        <LField label="Staff name" tipKey="staff_pin" showHint={showHint}>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="onb-staff-name" />
        </LField>
        <LField label="4-digit PIN" tipKey="staff_pin" showHint={showHint}>
          <Input value={draft.pin} onChange={(e) => setDraft({ ...draft, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono" data-testid="onb-staff-pin" />
        </LField>
        <Button onClick={add} disabled={adding} className="bg-sky-500 hover:bg-sky-600 text-white h-10" data-testid="onb-staff-add">
          {adding ? "Adding…" : "Add staff"}
        </Button>
      </div>
      {staff.length === 0 && (
        <p className="text-[11px] text-muted-foreground italic">Skip if you&apos;re a solo operator.</p>
      )}
    </div>
  );
}


// ---------- Section 6: Templates --------------------------------------------
const STARTER_TEMPLATES = [
  { name: "Pump Hire", default_price: 250, default_duration_minutes: 120 },
  { name: "Pump Hire + Piping", default_price: 350, default_duration_minutes: 180 },
  { name: "Pump Hire + Extra Load", default_price: 450, default_duration_minutes: 240 },
];

function TemplatesForm({ showHint, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const refresh = async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data || []); } catch (_e) { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const createStarter = async (tpl) => {
    setCreating(true);
    try {
      await api.post("/templates", tpl);
      toast.success(`Added template: ${tpl.name}`);
      await refresh();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const existingNames = new Set(templates.map((t) => (t.name || "").toLowerCase()));

  return (
    <div className="space-y-4">
      {templates.length > 0 && (
        <div className="space-y-1" data-testid="onb-templates-list">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between bg-secondary/30 rounded px-3 py-1.5 text-sm">
              <span className="font-semibold">{t.name}</span>
              <span className="mono text-[11px] tabular-nums text-muted-foreground">€{Number(t.default_price || 0).toFixed(2)} · {t.default_duration_minutes}min</span>
            </div>
          ))}
        </div>
      )}
      <div className="space-y-2" data-testid="onb-starter-templates">
        <Label className="text-[11px] uppercase tracking-wider font-bold">Add starter templates</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {STARTER_TEMPLATES.map((tpl) => {
            const already = existingNames.has(tpl.name.toLowerCase());
            return (
              <Button
                key={tpl.name}
                variant={already ? "ghost" : "outline"}
                onClick={() => createStarter(tpl)}
                disabled={creating || already}
                className="h-auto py-3 flex-col items-start text-left"
                data-testid={`onb-template-starter-${tpl.name.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <span className="font-bold">{tpl.name}</span>
                <span className="text-[10px] text-muted-foreground mono">€{tpl.default_price} · {tpl.default_duration_minutes}min</span>
                {already && <span className="text-[10px] text-emerald-700 mt-1">Added ✓</span>}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ---------- Section 7: Integrations -----------------------------------------
function IntegrationsForm({ showHint, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data));
  }, []);
  if (!data) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", data);
      toast.success("Integration settings saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <LField label="OpenRouteService API key" tipKey="ors_api_key" showHint={showHint}>
        <Input
          value={data.ors_api_key || ""}
          onChange={(e) => set("ors_api_key", e.target.value)}
          placeholder="Paste your ORS key — optional"
          className="font-mono text-xs"
          data-testid="onb-ors-key"
        />
      </LField>
      {/* Phase P.10 — Stripe Connect blurb hidden while Connect is
          feature-flagged off (see lib/featureFlags.js). */}
      {CONNECT_ENABLED && (
        <div className="text-[11px] text-muted-foreground bg-secondary/30 border border-border rounded p-3">
          <strong>Stripe Connect</strong> setup lives on the Billing page —
          you&apos;ll be prompted to connect your account the first time a customer
          pays online via the Pay-online button.
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-integrations-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save integration keys"}
        </Button>
      </div>
    </div>
  );
}


// ---------- Section 8: Daily backup -----------------------------------------
function BackupForm({ showHint, onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/backup/config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return <div className="text-sm text-muted-foreground italic py-6">Loading…</div>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/backup/config", {
        email: cfg.email, time: cfg.time, timezone: cfg.timezone,
        enabled: !!cfg.enabled, sheets_enabled: cfg.sheets_enabled,
      });
      setCfg(data);
      toast.success("Daily backup configured");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set("enabled", !!v)} data-testid="onb-backup-toggle" />
        <span>Send me a daily Excel backup at the time below</span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <LField label="Email" tipKey="company_email" showHint={showHint}>
          <Input value={cfg.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="onb-backup-email" />
        </LField>
        <LField label="Time" tipKey="company_email" showHint={showHint}>
          <Input type="time" value={cfg.time || "06:00"} onChange={(e) => set("time", e.target.value)} data-testid="onb-backup-time" />
        </LField>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wider font-bold">Timezone</Label>
          <Select value={cfg.timezone || "Europe/Dublin"} onValueChange={(v) => set("timezone", v)}>
            <SelectTrigger data-testid="onb-backup-tz"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Europe/Dublin">Europe/Dublin</SelectItem>
              <SelectItem value="Europe/London">Europe/London</SelectItem>
              <SelectItem value="UTC">UTC</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="onb-backup-save">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save backup schedule"}
        </Button>
      </div>
    </div>
  );
}
